// TonePickerPill — Iter172 (Randy pt-ytre-tone-picker, Feb 13, 2026)
//
// YTRE-only per-card TONE DELIVERY dropdown. Sits right of the Mold
// silo reaction pill on every redraftable comment card (SiloResponse
// + UserComment owner turn). Local per-card state (no persistence,
// resets on new session — same pattern as staged Mold reaction).
//
// The tone chosen here injects a "TONE DELIVERY" directive into the
// child re-draft's scaffold prompt (backend: _build_child_context_prompt
// reads `tone_override`). It overrides tonal delivery WITHOUT touching
// the silo's core voice on other turns.
//
// Registry lives at backend/routes/ytre.py::YTRE_TONE_PRESETS. Add /
// rename / remove tones there — frontend fetches via /api/ytre/tone-presets.

import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Waveform, Check, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Session-scoped cache — the tone catalog is static, so we hit the
// endpoint once per app-load per user and share across every YTRE
// card that mounts. Refresh happens naturally on full page reload.
let _cachedTones = null;
let _inFlight = null;
async function _fetchTones() {
  if (_cachedTones) return _cachedTones;
  if (_inFlight) return _inFlight;
  _inFlight = api.get("/ytre/tone-presets").then((r) => {
    _cachedTones = r.data?.presets || [];
    _inFlight = null;
    return _cachedTones;
  }).catch(() => {
    _inFlight = null;
    return [];
  });
  return _inFlight;
}

export default function TonePickerPill({ staged, onStage, disabled, entryId }) {
  const [open, setOpen] = useState(false);
  const [tones, setTones] = useState([]);
  useEffect(() => { _fetchTones().then(setTones); }, []);

  const label = staged?.label || "Tone";
  const isStaged = !!staged?.id;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`ytre-tone-btn-${entryId}`}
          title={isStaged
            ? `Tone: ${label} — applies to THIS card's next re-draft only.`
            : "Pick a tone delivery — applies to THIS card's next re-draft only."}
          className={`text-[11.5px] px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition ${
            isStaged
              ? "bg-[#EFEAFD] border-[#C9BCE8] text-[#4B3A7A] hover:bg-[#E4DCF9]"
              : "bg-white border-[#E4E6E1] text-[#4A5D4E] hover:bg-[#F0F1EE]/60"
          }`}
        >
          <Waveform size={11} weight="duotone" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-64 border hairline bg-[#FDFCF8] shadow-md"
        data-testid={`ytre-tone-menu-${entryId}`}
      >
        <div className="px-3 py-2 border-b hairline">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Tone delivery</div>
          <div className="text-[10.5px] text-[#6B6A68] mt-0.5 leading-snug">
            Override the silo&apos;s default warm voice for THIS reply only.
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {tones.length === 0 && (
            <div className="px-3 py-4 text-[11px] text-[#6B6A68] italic">Loading tones…</div>
          )}
          {tones.map((t) => {
            const active = staged?.id === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`ytre-tone-option-${entryId}-${t.id}`}
                onClick={() => { onStage(t); setOpen(false); }}
                className={`w-full text-left px-3 py-2 border-b hairline last:border-b-0 hover:bg-[#F0F1EE]/60 transition ${
                  active ? "bg-[#EFEAFD]" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-medium text-[#1C1B1A]">{t.label}</div>
                  {active && <Check size={11} weight="bold" className="text-[#4B3A7A] shrink-0" />}
                </div>
                <div className="text-[10.5px] text-[#6B6A68] mt-0.5 leading-snug">{t.hint}</div>
              </button>
            );
          })}
        </div>
        {isStaged && (
          <button
            type="button"
            data-testid={`ytre-tone-clear-${entryId}`}
            onClick={() => { onStage(null); setOpen(false); }}
            className="w-full px-3 py-2 border-t hairline text-[10.5px] uppercase tracking-[0.14em] text-[#6B6A68] hover:bg-[#F0F1EE] flex items-center justify-center gap-1.5"
          >
            <X size={10} weight="bold" />
            Clear · use silo default voice
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
